import { QueryFunctionContext, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { UrlUtilities, type QueryParams } from "src/http";
import { SignalRGroup } from "../../../components";
import { configuration } from "../../../configuration";
import { useSignalR } from "../../../hooks";
import { FetchError, GetUserTaskItemsDto } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";

const useGetUserTaskItemsRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function getUserTaskItemsRequest({
    queryKey,
  }: QueryFunctionContext<ReturnType<typeof queryKeys.taskItems.getUserTaskItems>>): Promise<GetUserTaskItemsDto> {
    const accessToken = await getAccessToken();
    const [, userGuid, queryParams] = queryKey;

    const url = new URL(`${configuration.apiRootUrl}/users/${userGuid}/task-items`);
    UrlUtilities.appendQueryParams(url, queryParams);

    const response = await fetch(url.href, {
      headers: {
        AUthorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new FetchError(response);
    }

    return response.json();
  }

  return { getUserTaskItemsRequest };
};

const useGetUserTaskItems = (userId: string | undefined, queryParams?: QueryParams) => {
  const { getUserTaskItemsRequest } = useGetUserTaskItemsRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  useEffect(() => {
    if (userId !== undefined) {
      signalR.joinGroup(SignalRGroup.UserTaskItems(userId));
      return () => signalR.leaveGroup(SignalRGroup.UserTaskItems(userId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    const handleShouldRefetchUserTaskItems = () => {
      queryClient.invalidateQueries(queryKeys.taskItems.allUserTaskItems);
    };

    signalR.onShouldRefetchUserTaskItems(handleShouldRefetchUserTaskItems);
    return () => signalR.offShouldRefetchUserTaskItems(handleShouldRefetchUserTaskItems);
  }, [queryClient, signalR]);

  return useQuery({
    queryKey: queryKeys.taskItems.getUserTaskItems(userId, queryParams),
    queryFn: getUserTaskItemsRequest,
    enabled: userId !== undefined,
  });
};

export { useGetUserTaskItems };
